import { Box, Button, Card, CardContent, CardHeader, FormHelperText, Grid, Drawer, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { ReasonCollectResponseDto } from "@/gen/api/governance";


const validationSchema =
  Yup.object().shape({
    reasonText: Yup.string().max(512, 'Reason Text must be at most 512 characters').required('Reason Text is required'),
  })

const EditReasonCollect: FC<{
  open: boolean;
  setOpen: (x: boolean) => void;
}> = ({ open, setOpen }) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const record = useRecordContext<ReasonCollectResponseDto & RecPatch>()
  const mode = record !== null && record !== undefined ? 'edit' : 'create'
  const save = useSimpleSaveAction()
  
  const initialValues = {
    submit: '',
    reasonText: record?.reasonText || '',
  }

  const preDataRef = useRef<boolean | null>(!!record)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setOpen(false)
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    save(mode === 'create' ? values : {...record, ...values}, options)
  }, [save, mode, setOpen])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm, setValues } = formikCtrl
  const handleClose = useCallback(() => { 
    setOpen(false) 
    preDataRef.current = null
    resetForm({
      values: { reasonText: '', submit: '', }
    })
  }, [setOpen, resetForm])


  useEffect(() => {
    if (!preDataRef.current && !!record) {
      preDataRef.current = true
      setValues({
        reasonText: record.reasonText || '',
      } as any)
    }
  }, [record?.id, open, setValues])

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardHeader title={mode === 'create' ? "New Reason for Collect" : "Edit Reason for Collect"} />
          <CardContent>
            <Grid
              container
              spacing={4}
            >
              <Grid item xs={12} >
                <TextField label="Reason Text*" name="reasonText" />
              </Grid>
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              disabled={isSubmitting}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Card>

      </OdForm>
    </Drawer>
  )
}

export default EditReasonCollect;
